import { produce } from 'immer';
import { RESET_DEV_DATA, SIGN_OUT } from 'store/auth/actions';
import { createReducer } from 'typesafe-actions';
import { UPGRADE } from 'store/actions';
import {
  doAsesListSet,
  doAsesListSetData,
  doAsesListSetFilter,
  doAsesListSetFilterDraft,
  doAsesListSetFilterOptions,
  doAsesListSetFilterOptionsDraft,
  doAsesListSetItem,
  doAsesListSetLoadStatus,
  doAsesListFilterLoad,
  doneAsesListFilterLoad,
  failAsesListFilterLoad,
} from './actions';
import { AsesState, AsesDefaultListFilter } from './types';
import { cloneDeep, intersection } from 'lodash';

const defaultListFilterOption: AsesState['listFilterOptions'] = {
  organisationRequired: true,
  organisations: [],
  assessmentTypes: [],
  propertyGroups: [],
  statuses: [],
  flags: [],
};

const initialState: AsesState = {
  listLoadStatus: { status: 'initial' },
  listLoadStatusEverLoaded: false,
  listFilterLoadStatus: null,
  listFilterLoadStatusEverLoaded: false,
  listFilter: cloneDeep(AsesDefaultListFilter),
  listFilterDraft: cloneDeep(AsesDefaultListFilter),
  listFilterOptionsDraft: cloneDeep(defaultListFilterOption),
  listFilterOptions: cloneDeep(defaultListFilterOption),
  listTotalCount: 0,
  listOffline: {},
  list: {},
  listPage: 0,
  listData: { assessmentStatuses: {} },
};

export const asesReducer = createReducer(initialState)
  .handleAction(SIGN_OUT.success, () => initialState)
  .handleAction(RESET_DEV_DATA, () => initialState)
  .handleAction(UPGRADE, (state) =>
    produce(state, (draft) => {
      draft.listFilterOptions = {
        ...defaultListFilterOption,
        ...draft.listFilterOptions,
      };
      draft.listFilter = {
        ...AsesDefaultListFilter,
        ...draft.listFilter,
      };
    }),
  )
  .handleAction(doAsesListSet, (state, action) => ({
    ...state,
    list: action.payload.append
      ? { ...state.list, ...action.payload.list }
      : { ...action.payload.list },
    listTotalCount: action.payload.totalCount ?? state.listTotalCount,
    listPage: action.payload.page,
  }))
  .handleAction(doAsesListSetItem, (state, action) =>
    produce(state, (draft) => {
      if (!action.payload.item) {
        delete draft.list[action.payload.id];
      }
      // Only update if row already exists
      else if (draft.list[action.payload.id]) {
        draft.list[action.payload.id] = {
          ...action.payload.item,
          property: {
            id: action.payload.item.property.id,
            name: action.payload.item.property.name,
            reference: action.payload.item.property.reference,
            customReference: action.payload.item.property.customReference,
          },
        };
      }
    }),
  )
  .handleAction(doAsesListSetData, (state, action) => ({
    ...state,
    listData: action.payload,
  }))
  .handleAction(doAsesListFilterLoad, (state) => ({
    ...state,
    listFilterLoadStatus: 'loading',
  }))
  .handleAction(doneAsesListFilterLoad, (state) =>
    produce(state, (draft) => {
      draft.listFilterLoadStatus = 'loaded';
      draft.listFilterLoadStatusEverLoaded = true;
    }),
  )
  .handleAction(failAsesListFilterLoad, (state) => ({
    ...state,
    listFilterLoadStatus: null,
  }))
  .handleAction(doAsesListSetFilterOptionsDraft, (state, action) =>
    produce(state, (draft) => {
      draft.listFilterOptionsDraft = {
        ...defaultListFilterOption,
        ...state.listFilterOptionsDraft,
        ...action.payload,
      };
      draft.listFilterDraft = {
        ...state.listFilterDraft,
        organisationIds: state.listFilterDraft.organisationIds.length
          ? intersection(
              draft.listFilterOptionsDraft.organisations.map((i) => i.id),
              state.listFilterDraft.organisationIds,
            )
          : [],
        statuses: state.listFilter.statuses.length
          ? intersection(
              draft.listFilterOptionsDraft.statuses.map((i) => i.id),
              state.listFilterDraft.statuses,
            )
          : [],
        propertyGroupIds: state.listFilterDraft.propertyGroupIds.length
          ? intersection(
              draft.listFilterOptionsDraft.propertyGroups.map((i) => i.id),
              state.listFilterDraft.propertyGroupIds,
            )
          : [],
      };
    }),
  )
  .handleAction(doAsesListSetFilterOptions, (state, action) =>
    produce(state, (draft) => {
      draft.listFilterOptions = {
        ...defaultListFilterOption,
        ...state.listFilterOptions,
        ...action.payload,
      };
      // Ensure existing filter is valid
      draft.listFilter = {
        ...state.listFilter,
        organisationIds: state.listFilter.organisationIds.length
          ? intersection(
              draft.listFilterOptions.organisations.map((i) => i.id),
              state.listFilter.organisationIds,
            )
          : [],
        statuses: state.listFilter.statuses.length
          ? intersection(
              draft.listFilterOptions.statuses.map((i) => i.id),
              state.listFilter.statuses,
            )
          : [],
        propertyGroupIds: state.listFilter.propertyGroupIds.length
          ? intersection(
              draft.listFilterOptions.propertyGroups.map((i) => i.id),
              state.listFilter.propertyGroupIds,
            )
          : [],
      };
    }),
  )
  .handleAction(doAsesListSetFilter, (state, action) =>
    produce(state, (draft) => {
      draft.listFilter = action.payload.append
        ? {
            ...initialState.listFilter,
            ...state.listFilter,
            ...action.payload.data,
          }
        : { ...initialState.listFilter, ...action.payload.data };
    }),
  )
  .handleAction(doAsesListSetFilterDraft, (state, action) =>
    produce(state, (draft) => {
      draft.listFilterDraft = action.payload.append
        ? {
            ...initialState.listFilter,
            ...state.listFilterDraft,
            ...action.payload.data,
          }
        : { ...initialState.listFilterDraft, ...action.payload.data };
    }),
  )
  .handleAction(doAsesListSetLoadStatus, (state, action) =>
    produce(state, (draft) => {
      draft.listLoadStatus = action.payload;
      draft.listLoadStatusEverLoaded =
        draft.listLoadStatusEverLoaded || action.payload?.status === 'loaded';
    }),
  );
